<template>
  <div class="staff-community-fee-summary-container">
    <!-- 搜索栏 -->
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>员工费用汇总查询</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-date-picker
            v-model="searchForm.startDate"
            type="date"
            placeholder="开始日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="w-100"
          />
        </el-col>
        <el-col :span="6">
          <el-date-picker
            v-model="searchForm.endDate"
            type="date"
            placeholder="结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="w-100"
          />
        </el-col>

        <el-col :span="6" class="text-right">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 主体：左侧员工列表，右侧两级明细 -->
    <el-row :gutter="20" class="mt-20 main-area">
      <!-- 左侧：员工列表 -->
      <el-col :span="6">
        <el-card class="box-card staff-card" v-loading="staffLoading">
          <div slot="header" class="clearfix">
            <span>员工列表</span>
          </div>
          <div class="staff-list">
            <div
              v-for="staff in staffList"
              :key="staff.tel || staff.staffIds"
              class="staff-item"
              :class="{ active: currentStaff && (currentStaff.tel || currentStaff.staffIds) === (staff.tel || staff.staffIds) }"
              @click="handleStaffClick(staff)"
            >
              <span class="staff-name">{{ staff.staffName }}</span>
              <span class="staff-community-count">{{ staff.address || '' }} {{ staff.communityCount }} 个小区</span>
            </div>
            <div v-if="!staffLoading && staffList.length === 0" class="empty-tip">
              暂无员工数据
            </div>
          </div>
        </el-card>

        <!-- 员工名下小区总体汇总（收费率等） -->
        <el-card v-if="currentStaff" class="box-card mt-10 staff-summary-card" v-loading="summaryLoading">
          <div slot="header" class="clearfix">
            <span>总体汇总</span>
            <span class="tip-text">{{ currentStaff.staffName }}</span>
          </div>
          <div v-if="hasSummary" class="summary-body">
            <div class="summary-rate">
              <div class="rate-title">收费率</div>
              <div class="rate-value" :style="{ color: rateColor(feeRate) }">{{ formatRate(feeRate) }}</div>
              <div class="rate-sub">
                户收费率 <span :style="{ color: rateColor(roomFeeRate) }">{{ formatRate(roomFeeRate) }}</span>
                &nbsp;·&nbsp; 清缴率 <span :style="{ color: rateColor(clearRate) }">{{ formatRate(clearRate) }}</span>
              </div>
            </div>
            <ul class="summary-items">
              <li><span>管理小区</span><b>{{ summary.communityCount }} 个</b></li>
              <li><span>当期应收（元）</span><b>{{ formatMoney(curReceivableFee) }}</b></li>
              <li><span>当期实收（元）</span><b>{{ formatMoney(curReceivedFee) }}</b></li>
              <li><span>当期欠费（元）</span><b>{{ formatMoney(curOweFee) }}</b></li>
              <li><span>历史欠费（元）</span><b>{{ formatMoney(hisOweFee) }}</b></li>
              <li><span>收费户 / 欠费户</span><b>{{ feeRoomCount }} / {{ oweRoomCount }}</b></li>
            </ul>
          </div>
          <div v-else class="empty-tip small">暂无汇总数据</div>
        </el-card>
      </el-col>

      <!-- 右侧：小区总额 → 费用项明细 -->
      <el-col :span="18">
        <div v-if="!currentStaff" class="empty-tip">
          请点击左侧员工姓名查看其名下小区的收费情况
        </div>

        <template v-else>
          <!-- 第一级：员工名下小区总额列表 -->
          <el-card class="box-card" v-loading="communityLoading">
            <div slot="header" class="clearfix">
              <span>{{ currentStaff.staffName }} — 名下小区收费汇总（{{ searchForm.startDate }} 至 {{ searchForm.endDate }}）</span>
              <div class="header-actions">
                <span class="tip-text">点击小区行或「查看详情」即可在行下方展开费用大项明细</span>
                <el-button type="primary" size="small" :loading="exportLoading" @click="handleExport">导出</el-button>
              </div>
            </div>
            <el-table
              :data="communityData"
              border
              style="width: 100%"
              :row-key="row => row.communityId"
              :expand-row-keys="expandedKeys"
              :row-class-name="communityRowClassName"
              @row-click="handleCommunityClick"
            >
              <!-- 展开行：该小区按系统费用大项 × 年月的应收/实收/欠费明细 -->
              <el-table-column type="expand">
                <template slot-scope="scope">
                  <div
                    v-if="selectedCommunity && scope.row.communityId === selectedCommunity.communityId"
                    v-loading="detailLoading"
                    class="community-detail-panel"
                  >
                    <div class="detail-summary">
                      <span class="detail-summary-item">小区总体：</span>
                      <span class="detail-summary-item">应收 <b>¥{{ formatMoney(selectedCommunity.receivableAmountTotal) }}</b></span>
                      <span class="detail-summary-item">实收 <b>¥{{ formatMoney(selectedCommunity.receivedAmountTotal) }}</b></span>
                      <span class="detail-summary-item">欠费 <b>¥{{ formatMoney(selectedCommunity.oweAmountTotal) }}</b></span>
                      <span class="detail-summary-item">收费率
                        <b :style="{ color: rateColor(rowFeeRate(selectedCommunity)) }">{{ formatRate(rowFeeRate(selectedCommunity)) }}</b>
                      </span>
                      <span class="detail-summary-item">户收费率
                        <b :style="{ color: rateColor(roomRateOfCommunity(selectedCommunity)) }">{{ formatRate(roomRateOfCommunity(selectedCommunity)) }}</b>
                      </span>
                    </div>
                    <el-table :data="detailData" border size="mini" style="width: 100%">
                      <el-table-column prop="feeTypeName" label="费用大项" align="center" min-width="140" />
                      <el-table-column prop="detailYear" label="年份" align="center" width="90" />
                      <el-table-column prop="detailMonth" label="月份" align="center" width="90" />
                      <el-table-column prop="receivableAmountTotal" label="应收金额（元）" align="right" min-width="140">
                        <template slot-scope="inner">
                          {{ formatMoney(inner.row.receivableAmountTotal) }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="receivedAmountTotal" label="实收金额（元）" align="right" min-width="140">
                        <template slot-scope="inner">
                          {{ formatMoney(inner.row.receivedAmountTotal) }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="oweAmountTotal" label="欠费金额（元）" align="right" min-width="140">
                        <template slot-scope="inner">
                          {{ formatMoney(inner.row.oweAmountTotal) }}
                        </template>
                      </el-table-column>
                      <el-table-column label="收费率" align="center" width="100">
                        <template slot-scope="inner">
                          <span :style="{ color: rateColor(rowFeeRate(inner.row)) }">
                            {{ formatRate(rowFeeRate(inner.row)) }}
                          </span>
                        </template>
                      </el-table-column>
                    </el-table>
                    <el-pagination
                      class="mt-10 text-right"
                      @size-change="handleDetailSizeChange"
                      @current-change="handleDetailCurrentChange"
                      :current-page="detailPagination.page"
                      :page-sizes="[10, 20, 50, 100]"
                      :page-size="detailPagination.row"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="detailPagination.total"
                    />
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="communityName" label="小区名称" align="center" min-width="150" />
              <el-table-column prop="totalRoomCount" label="总户数" align="center" width="90" />
              <el-table-column prop="feeRoomCount" label="收费户" align="center" width="90" />
              <el-table-column prop="oweRoomCount" label="欠费户" align="center" width="90" />
              <el-table-column prop="receivableAmountTotal" label="应收金额（元）" align="right" min-width="130">
                <template slot-scope="scope">
                  {{ formatMoney(scope.row.receivableAmountTotal) }}
                </template>
              </el-table-column>
              <el-table-column prop="receivedAmountTotal" label="实收金额（元）" align="right" min-width="130">
                <template slot-scope="scope">
                  {{ formatMoney(scope.row.receivedAmountTotal) }}
                </template>
              </el-table-column>
              <el-table-column prop="oweAmountTotal" label="欠费金额（元）" align="right" min-width="130">
                <template slot-scope="scope">
                  {{ formatMoney(scope.row.oweAmountTotal) }}
                </template>
              </el-table-column>
              <el-table-column label="收费率" align="center" width="110">
                <template slot-scope="scope">
                  <span :style="{ color: rateColor(rowFeeRate(scope.row)), fontWeight: 600 }">
                    {{ formatRate(rowFeeRate(scope.row)) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="户收费率" align="center" width="110">
                <template slot-scope="scope">
                  <span :style="{ color: rateColor(roomRateOfCommunity(scope.row)), fontWeight: 600 }">
                    {{ formatRate(roomRateOfCommunity(scope.row)) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="操作" align="center" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" @click.stop="handleCommunityClick(scope.row)">查看详情</el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              class="mt-10 text-right"
              @size-change="handleCommunitySizeChange"
              @current-change="handleCommunityCurrentChange"
              :current-page="communityPagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="communityPagination.row"
              layout="total, sizes, prev, pager, next, jumper"
              :total="communityPagination.total"
            />
          </el-card>

        </template>
      </el-col>
    </el-row>

    <!-- 指标说明 -->
    <el-card class="box-card mt-20">
      <el-collapse v-model="explainActive">
        <el-collapse-item name="explain">
          <template slot="title">
            <i class="el-icon-info explain-icon"></i>
            <span>指标说明</span>
          </template>
          <el-table :data="explainIndicators" border size="mini">
            <el-table-column prop="name" label="指标" width="160" />
            <el-table-column prop="desc" label="定义" />
          </el-table>
        </el-collapse-item>
      </el-collapse>
    </el-card>
  </div>
</template>

<script>
import {
  getStaffCommunityFeeSummary,
  getStaffCommunitySummary,
  getCommunityFeeDetail
} from '@/api/report/staffCommunityFeeSummaryApi'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  name: 'StaffCommunityFeeSummaryList',
  data() {
    return {
      searchForm: {
        startDate: '',
        endDate: '',
        feeTypeCd: ''
      },
      feeTypeOptions: [],
      staffList: [],
      staffLoading: false,
      currentStaff: null,
      communityData: [],
      communityLoading: false,
      exportLoading: false,
      communityPagination: {
        page: 1,
        row: 10,
        total: 0
      },
      selectedCommunity: null,
      // 当前展开（查看详情）的小区行 key，单行展开控制
      expandedKeys: [],
      detailData: [],
      detailLoading: false,
      detailPagination: {
        page: 1,
        row: 20,
        total: 0
      },
      // 员工总体汇总（detail=0，收费率等）
      summary: {},
      summaryLoading: false,
      // 底部指标说明默认展开
      explainActive: ['explain']
    }
  },
  computed: {
    hasSummary() {
      const s = this.summary
      return !!(s && s.staffIds)
    },
    // 页面各名词/指标的定义说明
    explainIndicators() {
      return [
        { name: '当期', desc: '页面上选择的查询区间（开始日期 至 结束日期），默认当月1日至月末；以下所有“当期”指标均按账单账期落在该区间内统计' },
        { name: '管理小区', desc: '员工名下绑定的、去重后的小区数量' },
        { name: '总户数', desc: '小区在册在用房屋总数（含住宅、商铺、车位等收费对象对应的房屋）' },
        { name: '收费户', desc: '有在用收费账单的房屋数。汇总卡按存在在用缴费单的房屋统计；小区行按查询区间内存在账单（应收或实收）的房屋统计' },
        { name: '欠费户', desc: '欠费未缴清的房屋数。汇总卡按欠费汇总表统计（含历史欠费户）；小区行按查询区间内应收 > 实收的房屋统计' },
        { name: '历史欠费', desc: '查询开始日期之前各月份已形成、截至查询日期仍未缴清的账单总额' },
        { name: '当期应收', desc: '查询区间内账单的应收金额合计（不管是否已收）' },
        { name: '当期实收', desc: '当期应收中已收取（缴清）的部分 = 当期应收 − 当期欠费' },
        { name: '当期欠费', desc: '查询区间内账单尚未收取的金额 = 当期应收 − 当期实收' },
        { name: '应收金额', desc: '表格某一维度（小区，或小区×费用大项×年月）在查询区间内的应收总额' },
        { name: '实收金额', desc: '表格某一维度（小区，或小区×费用大项×年月）在查询区间内实际收取的总额' },
        { name: '欠费金额', desc: '应收金额 − 实收金额，即该维度下尚未收取的金额' },
        { name: '收费率', desc: '当期实收 ÷ 当期应收 × 100%，查询区间内当期账单的收回比例（>=90% 绿、>=60% 橙、其余红）' },
        { name: '户收费率', desc: '(收费户 − 欠费户) ÷ 收费户 × 100%，收费户中已无欠费的户占比' },
        { name: '清缴率', desc: '区间实收 ÷ (区间实收 + 历史欠费) × 100%，反映对历史欠费的回收能力' },
        { name: '费用大项', desc: '系统费用类型（如物业费、水费、电费、停车费、租金等），是费用配置所属的大类；展开明细按“费用大项 × 年份 × 月份”逐行统计，同一大项下的多条收费配置自动合并' },
        { name: '年份 / 月份', desc: '账单归属的账期，即“该大项哪一年哪一月应该收取”' }
      ]
    },
    curReceivableFee() {
      return Number(this.summary.curReceivableFee || 0)
    },
    curOweFee() {
      return Number(this.summary.curOweFee || 0)
    },
    hisOweFee() {
      return Number(this.summary.hisOweFee || 0)
    },
    // 当期实收 = 当期应收 − 当期欠费
    curReceivedFee() {
      return this.curReceivableFee - this.curOweFee
    },
    feeRoomCount() {
      return Number(this.summary.feeRoomCount || 0)
    },
    oweRoomCount() {
      return Number(this.summary.oweRoomCount || 0)
    },
    // 收费率 = 当期实收 ÷ 当期应收
    feeRate() {
      return this.curReceivableFee > 0 ? (this.curReceivedFee / this.curReceivableFee * 100) : 0
    },
    // 户收费率 = (收费户 − 欠费户) ÷ 收费户
    roomFeeRate() {
      return this.feeRoomCount > 0 ? (this.feeRoomCount - this.oweRoomCount) / this.feeRoomCount * 100 : 0
    },
    // 清缴率 = 历史实收 ÷ (历史实收 + 历史欠费)
    clearRate() {
      const hisReceived = Number(this.summary.hisReceivedFee || 0)
      const denom = hisReceived + this.hisOweFee
      return denom > 0 ? hisReceived / denom * 100 : 0
    }
  },
  created() {
    this.initDate()
    this.loadFeeTypes()
    this.loadStaffList()
  },
  methods: {
    initDate() {
      const today = new Date()
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      this.searchForm.startDate = this.formatDate(firstDay)
      this.searchForm.endDate = this.formatDate(lastDay)
    },
    loadFeeTypes() {
      this.$store.dispatch('dict/getDict', {
        dictType: 'pay_fee_config',
        dictName: 'fee_type_cd'
      }).then(data => {
        this.feeTypeOptions = data
      })
    },
    // 查询员工列表（不带 staffId）
    async loadStaffList() {
      try {
        this.staffLoading = true
        const { data } = await getStaffCommunityFeeSummary({
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate
        })
        this.staffList = data || []
        // 如果当前选中的员工仍在列表中，刷新其小区汇总
        if (this.currentStaff) {
          const curKey = this.currentStaff.tel || this.currentStaff.staffIds
          const still = this.staffList.find(s => (s.tel || s.staffIds) === curKey)
          if (still) {
            this.communityPagination.page = 1
            this.loadStaffCommunities(still)
            this.loadStaffSummary(still)
          } else {
            this.currentStaff = null
            this.selectedCommunity = null
            this.expandedKeys = []
            this.detailData = []
            this.communityData = []
          }
        }
      } catch (error) {
        this.$message.error('获取员工列表失败')
      } finally {
        this.staffLoading = false
      }
    },
    // 点击员工，查询其名下小区总额列表 + 总体汇总
    handleStaffClick(staff) {
      this.currentStaff = staff
      this.selectedCommunity = null
      this.expandedKeys = []
      this.detailData = []
      this.communityPagination.page = 1
      this.communityPagination.total = 0
      this.loadStaffCommunities(staff)
      this.loadStaffSummary(staff)
    },
    // 查询员工名下所有小区的总体汇总（detail=0）
    async loadStaffSummary(staff) {
      if (!staff || !staff.staffIds) {
        this.summary = {}
        return
      }
      try {
        this.summaryLoading = true
        const params = {
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate,
          staffIds: staff.staffIds
        }
        if (this.searchForm.feeTypeCd) {
          params.feeTypeCd = this.searchForm.feeTypeCd
        }
        const { data } = await getStaffCommunityFeeSummary(params)
        this.summary = (data && data.length > 0) ? data[0] : {}
      } catch (error) {
        this.summary = {}
        this.$message.error('获取员工总体汇总失败')
      } finally {
        this.summaryLoading = false
      }
    },
    // 查询员工名下每个小区的费用总额（detail=1）
    async loadStaffCommunities(staff) {
      if (!staff || !staff.staffIds) {
        this.communityData = []
        return
      }
      try {
        this.communityLoading = true
        const params = {
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate,
          staffIds: staff.staffIds,
          page: this.communityPagination.page,
          row: this.communityPagination.row
        }
        if (this.searchForm.feeTypeCd) {
          params.feeTypeCds = this.searchForm.feeTypeCd
        }
        const res = await getStaffCommunitySummary(params)
        this.communityData = res.data || []
        this.communityPagination.total = res.total || 0
        // 若当前选中的小区仍在该员工名下，刷新其费用项明细
        if (this.selectedCommunity) {
          const still = this.communityData.find(c => c.communityId === this.selectedCommunity.communityId)
          if (still) {
            this.selectedCommunity = still
            this.loadCommunityDetail(still)
          } else {
            // 展开中的小区已不在当前列表（如换页），整体收起
            this.selectedCommunity = null
            this.expandedKeys = []
            this.detailData = []
            this.detailPagination = { page: 1, row: 20, total: 0 }
          }
        }
      } catch (error) {
        this.$message.error('获取小区费用汇总失败')
      } finally {
        this.communityLoading = false
      }
    },
    // 点击小区行 / 「查看详情」：在该小区行下方展开（或收起）其费用项明细（detail=2）
    handleCommunityClick(row) {
      if (!row || !row.communityId) {
        this.$message.warning('该小区暂无数据，无法查看详情')
        return
      }
      if (this.selectedCommunity && this.selectedCommunity.communityId === row.communityId) {
        // 再次点击同一小区：收起当前展开行
        this.collapseCommunityDetail()
        return
      }
      this.selectedCommunity = row
      this.expandedKeys = [row.communityId]
      this.detailData = []
      this.detailPagination.page = 1
      this.detailPagination.total = 0
      this.loadCommunityDetail(row)
    },
    // 收起当前小区的展开明细
    collapseCommunityDetail() {
      this.selectedCommunity = null
      this.expandedKeys = []
      this.detailData = []
      this.detailPagination = { page: 1, row: 20, total: 0 }
    },
    async loadCommunityDetail(community) {
      if (!community || !community.communityId) {
        this.detailData = []
        return
      }
      try {
        this.detailLoading = true
        const params = {
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate,
          communityId: community.communityId,
          // 按系统费用大项分组统计明细
          feeTypeGroup: '1',
          page: this.detailPagination.page,
          row: this.detailPagination.row
        }
        if (this.searchForm.feeTypeCd) {
          params.feeTypeCds = this.searchForm.feeTypeCd
        }
        const res = await getCommunityFeeDetail(params)
        this.detailData = res.data || []
        this.detailPagination.total = res.total || 0
      } catch (error) {
        this.$message.error('获取小区费用明细失败')
      } finally {
        this.detailLoading = false
      }
    },
    handleCommunitySizeChange(val) {
      this.communityPagination.row = val
      this.communityPagination.page = 1
      this.loadStaffCommunities(this.currentStaff)
    },
    handleCommunityCurrentChange(val) {
      this.communityPagination.page = val
      this.loadStaffCommunities(this.currentStaff)
    },
    handleDetailSizeChange(val) {
      this.detailPagination.row = val
      this.detailPagination.page = 1
      this.loadCommunityDetail(this.selectedCommunity)
    },
    handleDetailCurrentChange(val) {
      this.detailPagination.page = val
      this.loadCommunityDetail(this.selectedCommunity)
    },
    communityRowClassName({ row }) {
      return row.communityId === (this.selectedCommunity && this.selectedCommunity.communityId)
        ? 'current-community-row'
        : ''
    },
    handleSearch() {
      this.loadStaffList()
    },
    handleReset() {
      this.searchForm.feeTypeCd = ''
      this.initDate()
      this.currentStaff = null
      this.selectedCommunity = null
      this.expandedKeys = []
      this.communityData = []
      this.communityPagination = { page: 1, row: 10, total: 0 }
      this.detailData = []
      this.detailPagination = { page: 1, row: 20, total: 0 }
      this.summary = {}
      this.loadStaffList()
    },
    // 一键导出当前员工全部数据：总体汇总 + 名下小区收费汇总（每行一个小区，各费用类别横向展开；均不分页）
    async handleExport() {
      if (!this.currentStaff || !this.currentStaff.staffIds) {
        this.$message.warning('请先选择员工')
        return
      }
      try {
        this.exportLoading = true
        const params = {
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate,
          staffIds: this.currentStaff.staffIds,
          page: 1,
          row: 99999
        }
        if (this.searchForm.feeTypeCd) {
          params.feeTypeCds = this.searchForm.feeTypeCd
        }
        const res = await getStaffCommunitySummary(params)
        const rows = res.data || []
        if (!rows.length) {
          this.$message.warning('没有数据可导出')
          return
        }

        // 并发拉取各小区费用类别明细（不分页），按“小区 × 费用类别”汇总（跨年月合并）
        const communityCategoryMap = new Map() // communityId -> { [feeTypeName]: { receivable, received, owe } }
        const categoryOrder = []
        let detailFail = false
        let cursor = 0
        const workers = []
        for (let w = 0; w < Math.min(5, rows.length); w++) {
          workers.push((async () => {
            while (cursor < rows.length) {
              const community = rows[cursor++]
              try {
                const detailParams = {
                  startDate: this.searchForm.startDate,
                  endDate: this.searchForm.endDate,
                  communityId: community.communityId,
                  // 按系统费用大项分组统计明细
                  feeTypeGroup: '1',
                  page: 1,
                  row: 99999
                }
                if (this.searchForm.feeTypeCd) {
                  detailParams.feeTypeCds = this.searchForm.feeTypeCd
                }
                const dRes = await getCommunityFeeDetail(detailParams)
                const catMap = {}
                ;(dRes.data || []).forEach(d => {
                  const name = d.feeTypeName || '未分类'
                  if (!catMap[name]) {
                    catMap[name] = { receivable: 0, received: 0, owe: 0 }
                  }
                  catMap[name].receivable += Number(d.receivableAmountTotal || 0)
                  catMap[name].received += Number(d.receivedAmountTotal || 0)
                  catMap[name].owe += Number(d.oweAmountTotal || 0)
                })
                communityCategoryMap.set(community.communityId, catMap)
              } catch (error) {
                detailFail = true
              }
            }
          })())
        }
        await Promise.all(workers)

        // 费用类别列顺序：按小区顺序首次出现的顺序排列
        rows.forEach(row => {
          const catMap = communityCategoryMap.get(row.communityId) || {}
          Object.keys(catMap).forEach(name => {
            if (!categoryOrder.includes(name)) {
              categoryOrder.push(name)
            }
          })
        })

        // 表头：小区基础汇总 + 每个费用类别（应收 / 实收 / 欠费 / 收费率）
        const communityHeaders = ['小区名称', '总户数', '收费户', '欠费户', '应收金额（元）', '实收金额（元）', '欠费金额（元）', '收费率', '户收费率']
        categoryOrder.forEach(name => {
          communityHeaders.push(`${name}-应收（元）`, `${name}-实收（元）`, `${name}-欠费（元）`, `${name}-收费率`)
        })

        // 每行一个小区，费用类别横向展开
        const communityRows = rows.map(row => {
          const catMap = communityCategoryMap.get(row.communityId) || {}
          const line = [
            row.communityName || '',
            row.totalRoomCount || 0,
            row.feeRoomCount || 0,
            row.oweRoomCount || 0,
            this.formatMoney(row.receivableAmountTotal),
            this.formatMoney(row.receivedAmountTotal),
            this.formatMoney(row.oweAmountTotal),
            this.formatRate(this.rowFeeRate(row)),
            this.formatRate(this.roomRateOfCommunity(row))
          ]
          categoryOrder.forEach(name => {
            const c = catMap[name] || { receivable: 0, received: 0, owe: 0 }
            const rate = c.receivable > 0 ? c.received / c.receivable * 100 : 0
            line.push(
              this.formatMoney(c.receivable),
              this.formatMoney(c.received),
              this.formatMoney(c.owe),
              this.formatRate(rate)
            )
          })
          return line
        })

        const sheets = []
        // Sheet 1：总体汇总（与左侧卡片口径一致）
        sheets.push({
          name: '总体汇总',
          data: [['指标', '数值'], ...this.buildOverallExportRows()]
        })
        // Sheet 2：名下各小区收费汇总（每行一个小区，费用类别横向展开）
        sheets.push({ name: '小区收费汇总', data: [communityHeaders, ...communityRows] })

        if (detailFail) {
          this.$message.warning('部分小区费用类别明细获取失败，请重试或检查数据')
        }
        const fileName = `员工名下小区收费汇总_${this.currentStaff.staffName}_${this.searchForm.startDate}_${this.searchForm.endDate}`
        this.downloadExcel(sheets, fileName)
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    // 左侧总体汇总卡的指标数值（与页面显示口径一致）
    buildOverallExportRows() {
      return [
        ['管理小区（个）', Number(this.summary.communityCount || 0)],
        ['当期应收（元）', this.formatMoney(this.curReceivableFee)],
        ['当期实收（元）', this.formatMoney(this.curReceivedFee)],
        ['当期欠费（元）', this.formatMoney(this.curOweFee)],
        ['历史欠费（元）', this.formatMoney(this.hisOweFee)],
        ['收费户（户）', this.feeRoomCount],
        ['欠费户（户）', this.oweRoomCount],
        ['收费率', this.formatRate(this.feeRate)],
        ['户收费率', this.formatRate(this.roomFeeRate)],
        ['清缴率', this.formatRate(this.clearRate)]
      ]
    },
    downloadExcel(sheets, fileName) {
      const wb = XLSX.utils.book_new()
      sheets.forEach(sheet => {
        const ws = XLSX.utils.aoa_to_sheet(sheet.data)
        // 按内容自动设置列宽，便于阅读
        const head = sheet.data[0] || []
        ws['!cols'] = head.map((h, idx) => {
          let max = String(h).length
          sheet.data.forEach(row => {
            const cell = row[idx]
            if (cell !== undefined && cell !== null) {
              max = Math.max(max, String(cell).length)
            }
          })
          return { wch: Math.min(Math.max(max + 2, 8), 40) }
        })
        XLSX.utils.book_append_sheet(wb, ws, sheet.name)
      })
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], { type: 'application/octet-stream' })
      saveAs(blob, `${fileName}.xlsx`)
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      let month = '' + (d.getMonth() + 1)
      let day = '' + d.getDate()
      const year = d.getFullYear()
      if (month.length < 2) month = '0' + month
      if (day.length < 2) day = '0' + day
      return [year, month, day].join('-')
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatRate(value) {
      return Number(value || 0).toFixed(2) + '%'
    },
    // 单项（小区行 / 费用大项明细行）收费率 = 实收 ÷ 应收
    rowFeeRate(row) {
      if (!row) return 0
      const receivable = Number(row.receivableAmountTotal || 0)
      const received = Number(row.receivedAmountTotal || 0)
      return receivable > 0 ? received / receivable * 100 : 0
    },
    // 小区户收费率 = (收费户 − 欠费户) ÷ 收费户
    roomRateOfCommunity(row) {
      if (!row) return 0
      const feeRoomCount = Number(row.feeRoomCount || 0)
      const oweRoomCount = Number(row.oweRoomCount || 0)
      return feeRoomCount > 0 ? (feeRoomCount - oweRoomCount) / feeRoomCount * 100 : 0
    },
    rateColor(rate) {
      if ((rate || 0) >= 90) return '#67C23A'
      if ((rate || 0) >= 60) return '#E6A23C'
      return '#F56C6C'
    }
  }
}
</script>

<style lang="scss" scoped>
.staff-community-fee-summary-container {
  padding: 20px;

  .box-card {
    margin-bottom: 0;
  }

  .mt-20 {
    margin-top: 20px;
  }

  .mt-10 {
    margin-top: 10px;
  }

  .w-100 {
    width: 100%;
  }

  .text-right {
    text-align: right;
  }

  .tip-text {
    float: right;
    font-size: 12px;
    color: #909399;
    font-weight: normal;
  }

  .header-actions {
    float: right;
    display: flex;
    align-items: center;

    .tip-text {
      float: none;
      margin-right: 10px;
    }
  }

  .community-detail-panel {
    padding: 12px 20px;
    background: #fafafa;

    .detail-summary {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
      padding: 4px 2px 12px;
      font-size: 12px;
      color: #606266;

      .detail-summary-item {
        b {
          color: #303133;
          font-weight: 600;
        }
      }
    }
  }

  .main-area {
    .staff-card {
      margin-bottom: 0;
    }
  }

  .staff-summary-card {
    .summary-body {
      padding: 0 4px;
    }

    .summary-rate {
      padding: 6px 0 12px;
      text-align: center;
      border-bottom: 1px dashed #ebeef5;

      .rate-title {
        font-size: 12px;
        color: #909399;
      }

      .rate-value {
        font-size: 26px;
        font-weight: bold;
        line-height: 1.5;
      }

      .rate-sub {
        margin-top: 2px;
        font-size: 12px;
        color: #606266;
      }
    }

    .summary-items {
      margin: 0;
      padding: 8px 0 2px;
      list-style: none;

      li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px 2px;
        font-size: 12px;
        color: #606266;

        b {
          color: #303133;
          font-weight: 600;
        }
      }
    }
  }

  .staff-list {
    max-height: 500px;
    overflow-y: auto;

    .staff-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 10px;
      cursor: pointer;
      border-bottom: 1px solid #ebeef5;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f5f7fa;
      }

      &.active {
        background-color: #ecf5ff;
        color: #409EFF;

        .staff-community-count {
          color: #409EFF;
        }
      }

      .staff-name {
        font-size: 14px;
        font-weight: bold;
      }

      .staff-community-count {
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .empty-tip {
    padding: 60px 0;
    text-align: center;
    color: #909399;

    &.small {
      padding: 40px 0;
    }
  }

  .explain-icon {
    margin-right: 6px;
    color: #409eff;
  }
}

::v-deep .current-community-row {
  background-color: #ecf5ff !important;
  cursor: pointer;
}

::v-deep .el-table__row {
  cursor: pointer;
}

// 展开动作统一由「点击行 / 查看详情」控制，隐藏默认展开箭头
::v-deep .el-table__expand-icon {
  display: none;
}
</style>
